import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { 
  ArrowLeft, ArrowRight, Sparkles, Calendar as CalendarIcon, 
  CheckCircle, Loader2, Plus, Minus, Info, CreditCard,
  Monitor, Globe, QrCode, Share2, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const usageScopeOptions = [
  { value: 'single_event', label: 'Single Event', desc: 'One-time activation for a specific event' },
  { value: 'multi_event', label: 'Multi-Event', desc: 'Series of related events' },
  { value: 'campaign', label: 'Campaign', desc: 'Extended marketing campaign' },
  { value: 'ongoing', label: 'Ongoing / Retainer', desc: 'Continuous monthly usage' }
];

const deploymentTypeOptions = [
  { value: 'live_event', label: 'Live Event', icon: Sparkles, desc: 'In-person event activation' },
  { value: 'website_embed', label: 'Website Embed', icon: Globe, desc: 'Embedded on your website' },
  { value: 'qr_experience', label: 'QR Experience', icon: QrCode, desc: 'QR code activated interactions' },
  { value: 'social_campaign', label: 'Social Campaign', icon: Share2, desc: 'Social media integration' },
  { value: 'multi_channel', label: 'Multi-Channel', icon: Layers, desc: 'Multiple platforms combined' }
];

const AmbassadorBookingWizard = () => {
  const { id: ambassadorId } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ambassador, setAmbassador] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [addons, setAddons] = useState([]);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    usage_scope: '',
    deployment_type: '',
    start_date: null,
    end_date: null,
    instances_count: 1,
    is_retainer: false,
    retainer_months: 1,
    event_name: '',
    event_location: '',
    event_description: '',
    campaign_name: '',
    campaign_objectives: '',
    client_notes: '',
    special_requirements: '',
    selectedAddons: {}  // { addon_code: quantity }
  });

  useEffect(() => {
    fetchData();
  }, [ambassadorId]);

  useEffect(() => {
    if (ambassador && formData.usage_scope) {
      calculatePrice();
    }
  }, [formData, ambassador]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ambRes, tiersRes, addonsRes] = await Promise.all([
        api.get(`/ambassadors/${ambassadorId}`),
        api.get('/booking/pricing-tiers'),
        api.get('/booking/addons')
      ]);
      setAmbassador(ambRes.data);
      setPricingTiers(tiersRes.data);
      setAddons(addonsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load booking information');
      navigate('/ambassadors');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    if (!formData.usage_scope || !formData.deployment_type || !formData.start_date) {
      setCalculatedPrice(null);
      return;
    }

    try {
      const addonsList = Object.entries(formData.selectedAddons)
        .filter(([_, qty]) => qty > 0)
        .map(([code, qty]) => ({ addon_code: code, quantity: qty }));

      const response = await api.post('/booking/calculate-price', {
        ambassador_id: ambassador.id,
        usage_scope: formData.usage_scope,
        deployment_type: formData.deployment_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : null,
        instances_count: formData.instances_count,
        addons: addonsList,
        is_retainer: formData.is_retainer,
        retainer_months: formData.is_retainer ? formData.retainer_months : null
      });
      setCalculatedPrice(response.data);
    } catch (error) {
      console.error('Failed to calculate price:', error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-set retainer for ongoing usage
      if (field === 'usage_scope' && value === 'ongoing') {
        updated.is_retainer = true;
      }
      
      return updated;
    });
  };

  const toggleAddon = (addonCode, maxQty = 1) => {
    setFormData(prev => ({
      ...prev,
      selectedAddons: {
        ...prev.selectedAddons,
        [addonCode]: prev.selectedAddons[addonCode] ? 0 : 1
      }
    }));
  };

  const updateAddonQuantity = (addonCode, delta, maxQty) => {
    setFormData(prev => {
      const currentQty = prev.selectedAddons[addonCode] || 0;
      const newQty = Math.max(0, Math.min(maxQty, currentQty + delta));
      return {
        ...prev,
        selectedAddons: {
          ...prev.selectedAddons,
          [addonCode]: newQty
        }
      };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const addonsList = Object.entries(formData.selectedAddons)
        .filter(([_, qty]) => qty > 0)
        .map(([code, qty]) => ({ addon_code: code, quantity: qty }));

      const response = await api.post('/booking/create', {
        ambassador_id: ambassador.id,
        booking_type: 'digital_activation',
        usage_scope: formData.usage_scope,
        deployment_type: formData.deployment_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: formData.end_date ? format(formData.end_date, 'yyyy-MM-dd') : null,
        instances_count: formData.instances_count,
        addons: addonsList,
        is_retainer: formData.is_retainer,
        retainer_months: formData.is_retainer ? formData.retainer_months : null,
        event_name: formData.event_name || null,
        event_location: formData.event_location || null,
        event_description: formData.event_description || null,
        campaign_name: formData.campaign_name || null,
        campaign_objectives: formData.campaign_objectives || null,
        client_notes: formData.client_notes || null,
        special_requirements: formData.special_requirements || null
      });

      const bookingId = response.data.booking_id;
      const requiresApproval = response.data.requires_approval;
      
      // If requires approval (Luxury/Custom), redirect to booking details
      if (requiresApproval) {
        toast.success('Booking submitted for approval!');
        navigate(`/client/ambassador-booking/${bookingId}`);
        return;
      }
      
      // For Standard/Premium, redirect to Stripe checkout
      try {
        const checkoutResponse = await api.post('/ambassador-checkout/create-session', {
          booking_id: bookingId,
          origin_url: window.location.origin
        });
        
        // Redirect to Stripe Checkout
        window.location.href = checkoutResponse.data.checkout_url;
      } catch (checkoutError) {
        console.error('Failed to create checkout session:', checkoutError);
        toast.error('Failed to create checkout session. Please try paying from your bookings page.');
        navigate(`/client/ambassador-booking/${bookingId}`);
      }
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.usage_scope && formData.deployment_type;
      case 2:
        return formData.start_date;
      case 3:
        return true; // Addons are optional
      case 4:
        return true; // Details are optional
      default:
        return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const tierInfo = pricingTiers.find(t => t.tier === ambassador?.tier);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ambassador-booking-wizard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate(`/ambassador/${ambassador.slug}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Ambassador Summary */}
        <Card className="bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <img
                src={ambassador.avatar_url}
                alt={ambassador.name}
                className="w-16 h-16 rounded-full border-2 border-white"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold">{ambassador.name}</h2>
                  <Badge className="bg-white/20 text-white capitalize">{ambassador.tier}</Badge>
                </div>
                <p className="text-white/80">{ambassador.tagline}</p>
              </div>
              {calculatedPrice && (
                <div className="text-right">
                  <p className="text-sm text-white/80">Total</p>
                  <p className="text-2xl font-bold">${calculatedPrice.total_price.toLocaleString()}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          {['Scope', 'Schedule', 'Add-ons', 'Details', 'Review'].map((label, idx) => (
            <div key={label} className="flex items-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                step > idx + 1 ? "bg-green-500 text-white" :
                step === idx + 1 ? "bg-purple-500 text-white" :
                "bg-slate-200 text-slate-500"
              )}>
                {step > idx + 1 ? <CheckCircle className="w-4 h-4" /> : idx + 1}
              </div>
              <span className={cn(
                "ml-2 text-sm hidden sm:block",
                step === idx + 1 ? "font-medium text-purple-600" : "text-muted-foreground"
              )}>
                {label}
              </span>
              {idx < 4 && (
                <div className={cn(
                  "w-8 sm:w-16 h-0.5 mx-2",
                  step > idx + 1 ? "bg-green-500" : "bg-slate-200"
                )} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="bg-white mb-6">
          {/* Step 1: Scope & Deployment */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle>Usage Scope & Deployment</CardTitle>
                <CardDescription>How will you use this AI Ambassador?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-base font-medium mb-3 block">Usage Scope</Label>
                  <RadioGroup
                    value={formData.usage_scope}
                    onValueChange={(value) => updateFormData('usage_scope', value)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    {usageScopeOptions.map((opt) => (
                      <div key={opt.value}>
                        <RadioGroupItem value={opt.value} id={opt.value} className="peer sr-only" />
                        <Label
                          htmlFor={opt.value}
                          className="flex flex-col p-4 border rounded-lg cursor-pointer hover:bg-slate-50 peer-data-[state=checked]:border-purple-500 peer-data-[state=checked]:bg-purple-50"
                        >
                          <span className="font-medium">{opt.label}</span>
                          <span className="text-sm text-muted-foreground">{opt.desc}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-base font-medium mb-3 block">Deployment Type</Label>
                  <RadioGroup
                    value={formData.deployment_type}
                    onValueChange={(value) => updateFormData('deployment_type', value)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    {deploymentTypeOptions.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <div key={opt.value}>
                          <RadioGroupItem value={opt.value} id={`deploy-${opt.value}`} className="peer sr-only" />
                          <Label
                            htmlFor={`deploy-${opt.value}`}
                            className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 peer-data-[state=checked]:border-purple-500 peer-data-[state=checked]:bg-purple-50"
                          >
                            <Icon className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <span className="font-medium block">{opt.label}</span>
                              <span className="text-sm text-muted-foreground">{opt.desc}</span>
                            </div>
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Schedule */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle>Schedule & Duration</CardTitle>
                <CardDescription>When do you need the AI Ambassador?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal mt-1",
                            !formData.start_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.start_date ? format(formData.start_date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => updateFormData('start_date', date)}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label>End Date {formData.usage_scope !== 'single_event' ? '*' : '(Optional)'}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal mt-1",
                            !formData.end_date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.end_date ? format(formData.end_date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => updateFormData('end_date', date)}
                          disabled={(date) => date < (formData.start_date || new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label>Number of Instances / Locations</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => updateFormData('instances_count', Math.max(1, formData.instances_count - 1))}
                      disabled={formData.instances_count <= 1}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-bold w-12 text-center">{formData.instances_count}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => updateFormData('instances_count', Math.min(20, formData.instances_count + 1))}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground ml-2">
                      {formData.instances_count > 1 ? 'Multi-screen/location deployment' : 'Single deployment'}
                    </span>
                  </div>
                </div>

                {formData.usage_scope === 'ongoing' && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox
                        id="is_retainer"
                        checked={formData.is_retainer}
                        onCheckedChange={(checked) => updateFormData('is_retainer', checked)}
                      />
                      <Label htmlFor="is_retainer" className="font-medium">Monthly Retainer Subscription</Label>
                    </div>
                    {formData.is_retainer && tierInfo?.retainer_price_monthly && (
                      <div className="mt-3">
                        <Label>Initial Commitment (months)</Label>
                        <Select
                          value={String(formData.retainer_months)}
                          onValueChange={(value) => updateFormData('retainer_months', parseInt(value))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 3, 6, 12].map((m) => (
                              <SelectItem key={m} value={String(m)}>
                                {m} {m === 1 ? 'month' : 'months'} - ${(tierInfo.retainer_price_monthly * m).toLocaleString()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </>
          )}

          {/* Step 3: Add-ons */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle>Add-ons & Customization</CardTitle>
                <CardDescription>Enhance your AI Ambassador experience</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {addons.map((addon) => {
                    const isSelected = formData.selectedAddons[addon.code] > 0;
                    const tierPrice = addon.tier_pricing[ambassador.tier] || addon.price.default;
                    
                    return (
                      <div
                        key={addon.code}
                        className={cn(
                          "p-4 border rounded-lg transition-colors",
                          isSelected ? "border-purple-500 bg-purple-50" : "hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={addon.code}
                              checked={isSelected}
                              onCheckedChange={() => toggleAddon(addon.code, addon.max_quantity)}
                            />
                            <div>
                              <Label htmlFor={addon.code} className="font-medium cursor-pointer">
                                {addon.name}
                              </Label>
                              <p className="text-sm text-muted-foreground">{addon.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">${tierPrice}</p>
                            {addon.requires_quantity && <p className="text-xs text-muted-foreground">per unit</p>}
                          </div>
                        </div>
                        
                        {addon.requires_quantity && isSelected && (
                          <div className="mt-3 flex items-center gap-3 ml-7">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateAddonQuantity(addon.code, -1, addon.max_quantity)}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="font-medium w-8 text-center">
                              {formData.selectedAddons[addon.code]}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateAddonQuantity(addon.code, 1, addon.max_quantity)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <span className="text-sm text-muted-foreground">
                              = ${tierPrice * formData.selectedAddons[addon.code]}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Details */}
          {step === 4 && (
            <>
              <CardHeader>
                <CardTitle>Event / Campaign Details</CardTitle>
                <CardDescription>Tell us more about your activation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(formData.usage_scope === 'single_event' || formData.usage_scope === 'multi_event') && (
                  <>
                    <div>
                      <Label htmlFor="event_name">Event Name</Label>
                      <Input
                        id="event_name"
                        value={formData.event_name}
                        onChange={(e) => updateFormData('event_name', e.target.value)}
                        placeholder="e.g., Annual Tech Conference 2026"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event_location">Event Location</Label>
                      <Input
                        id="event_location"
                        value={formData.event_location}
                        onChange={(e) => updateFormData('event_location', e.target.value)}
                        placeholder="e.g., Moscone Center, San Francisco"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="event_description">Event Description</Label>
                      <Textarea
                        id="event_description"
                        value={formData.event_description}
                        onChange={(e) => updateFormData('event_description', e.target.value)}
                        placeholder="Brief description of your event..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </>
                )}

                {(formData.usage_scope === 'campaign' || formData.usage_scope === 'ongoing') && (
                  <>
                    <div>
                      <Label htmlFor="campaign_name">Campaign Name</Label>
                      <Input
                        id="campaign_name"
                        value={formData.campaign_name}
                        onChange={(e) => updateFormData('campaign_name', e.target.value)}
                        placeholder="e.g., Summer Brand Launch"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="campaign_objectives">Campaign Objectives</Label>
                      <Textarea
                        id="campaign_objectives"
                        value={formData.campaign_objectives}
                        onChange={(e) => updateFormData('campaign_objectives', e.target.value)}
                        placeholder="What are your goals for this campaign?"
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="special_requirements">Special Requirements</Label>
                  <Textarea
                    id="special_requirements"
                    value={formData.special_requirements}
                    onChange={(e) => updateFormData('special_requirements', e.target.value)}
                    placeholder="Any specific requirements or customizations needed..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="client_notes">Additional Notes</Label>
                  <Textarea
                    id="client_notes"
                    value={formData.client_notes}
                    onChange={(e) => updateFormData('client_notes', e.target.value)}
                    placeholder="Any other information we should know..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 5: Review */}
          {step === 5 && calculatedPrice && (
            <>
              <CardHeader>
                <CardTitle>Review & Confirm</CardTitle>
                <CardDescription>Please review your booking details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Usage Scope</p>
                    <p className="font-medium capitalize">{formData.usage_scope.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Deployment</p>
                    <p className="font-medium capitalize">{formData.deployment_type.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Start Date</p>
                    <p className="font-medium">{format(formData.start_date, "PPP")}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Instances</p>
                    <p className="font-medium">{formData.instances_count}</p>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-4">Price Breakdown</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Base Activation ({calculatedPrice.tier})</span>
                      <span>${calculatedPrice.base_price.toLocaleString()}</span>
                    </div>
                    
                    {calculatedPrice.addons.length > 0 && (
                      <>
                        <div className="border-t my-2"></div>
                        {calculatedPrice.addons.map((addon) => (
                          <div key={addon.code} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {addon.name} {addon.quantity > 1 ? `x${addon.quantity}` : ''}
                            </span>
                            <span>${addon.total_price.toLocaleString()}</span>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {calculatedPrice.instance_fee > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Multi-instance fee</span>
                        <span>${calculatedPrice.instance_fee.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {calculatedPrice.is_retainer && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          Monthly Retainer ({calculatedPrice.retainer_months}mo)
                        </span>
                        <span>${calculatedPrice.retainer_total.toLocaleString()}</span>
                      </div>
                    )}
                    
                    <div className="border-t my-2"></div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span>${calculatedPrice.total_price.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Approval Notice */}
                {calculatedPrice.requires_approval && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Admin Approval Required</p>
                      <p className="text-sm text-amber-700">
                        {ambassador.tier === 'luxury' || ambassador.tier === 'custom'
                          ? `${ambassador.tier.charAt(0).toUpperCase() + ambassador.tier.slice(1)} tier bookings require admin approval before payment.`
                          : 'This booking requires admin review.'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {step < 5 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Booking...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {calculatedPrice?.requires_approval ? 'Submit for Approval' : 'Proceed to Payment'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmbassadorBookingWizard;
